import type { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';

import type { NLPSearchResultSource } from '../../../ai-answer-dialog/types';

type Node = {
	type: string;
	attrs?: unknown;
	text?: string;
	content?: unknown;
};

// Work on the accumulated document, since citation markers can span stream chunks.
export const deduplicateCitations = (
	adf: ReturnType<typeof encodeAdf>,
	sources: NLPSearchResultSource[],
): ReturnType<typeof encodeAdf> => {
	const seen = new Set<string>();
	const visit = <T extends Node>(node: T): T => {
		if (!Array.isArray(node.content)) {
			return node;
		}
		if (!['paragraph', 'heading', 'bulletList', 'orderedList'].includes(node.type)) {
			return { ...node, content: (node.content as Node[]).map(visit) };
		}

		let selected: Node | undefined;
		let selectedKey: string | undefined;
		let destination: Node | undefined;
		const strip = (current: Node): Node => {
			if (!Array.isArray(current.content)) {
				return current;
			}
			const content: Node[] = [];
			const result = { ...current, content };
			if (current.type === 'paragraph' || current.type === 'heading') {
				destination = result;
			}
			const children = current.content as Node[];
			for (const [position, child] of children.entries()) {
				const attrs = child.attrs as { localId?: string; text?: string } | undefined;
				if (
					child.type === 'status' &&
					attrs?.text === 'citation' &&
					typeof attrs.localId === 'string' &&
					/^\[\d+(,\d+)*\]$/.test(attrs.localId)
				) {
					if (!selected) {
						const index = (JSON.parse(attrs.localId) as number[]).find((index) => {
							if (sources.length && !sources[index - 1]?.url) {
								return false;
							}
							return !seen.has(sources[index - 1]?.url || String(index));
						});
						if (index !== undefined) {
							selectedKey = sources[index - 1]?.url || String(index);
							selected = { ...child, attrs: { ...attrs, localId: JSON.stringify([index]) } };
						}
					}
					// Remove parser-inserted spacing before punctuation or another space.
					const previous = content[content.length - 1];
					const next = children[position + 1];
					if (
						previous?.type === 'text' &&
						(!next || (next.type === 'text' && /^[\s.,;:!?]/.test(next.text ?? '')))
					) {
						content[content.length - 1] = { ...previous, text: previous.text?.trimEnd() };
					}
					continue;
				}
				content.push(strip(child));
			}
			return result;
		};
		const result = strip(node);
		if (selected && selectedKey && destination && Array.isArray(destination.content)) {
			destination.content.push({ type: 'text', text: ' ' }, selected);
			seen.add(selectedKey);
		}
		return { ...node, content: result.content };
	};
	return visit(adf);
};
